/* Two questions the homepage asks before spending bandwidth and main
   thread on decoration: "is this visitor on a connection or device where
   it hurts?" and "has the page itself finished arriving?". */

/** True when the visitor asked for less data (Save-Data), is on a real 2G
 *  connection, or has a 1 GB device. Browsers without the Network
 *  Information API (Safari, Firefox) report nothing and get the full
 *  experience.
 *
 *  Not "3g": Chrome derives `effectiveType` mostly from round-trip time
 *  and calls anything around 270 ms or slower 3G. That is ordinary mobile
 *  data in Mexico, most VPNs and plenty of office Wi-Fi, and treating it
 *  as constrained turned the homepage motion off for normal visitors.
 *  The motion already waits for the page to finish loading, so a 3G
 *  visitor's first paint doesn't pay for it. */
export function isConstrainedDevice() {
  if (typeof navigator === 'undefined') return false;
  const connection = navigator.connection;
  if (connection?.saveData) return true;
  if (['slow-2g', '2g'].includes(connection?.effectiveType)) return true;
  return typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 1;
}

/** Runs `callback` once the window `load` event has fired and the main
 *  thread is next idle. Returns a cancel function. */
export function afterLoadIdle(callback, timeout = 2500) {
  let idleId = 0, timerId = 0, done = false;
  const run = () => {
    if (done) return;
    if ('requestIdleCallback' in window) idleId = window.requestIdleCallback(() => { done = true; callback(); }, { timeout });
    else timerId = window.setTimeout(() => { done = true; callback(); }, 200);
  };
  if (document.readyState === 'complete') run();
  else window.addEventListener('load', run, { once: true });
  return () => {
    done = true;
    window.removeEventListener('load', run);
    if (idleId) window.cancelIdleCallback?.(idleId);
    if (timerId) window.clearTimeout(timerId);
  };
}
